import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Subscription, filter } from 'rxjs';

import { ToastService } from '#services/toast/toast.service';
import { isChunkLoadError } from '#lib/chunk-load-error';
import { logger } from '#lib/logger';

const RELOAD_TIMESTAMP_KEY = 'lyfeplum.stale-bundle-reload-at';

/**
 * How long to stay quiet after a reload. Long enough that a genuinely broken deploy stops
 * nagging, short enough that a later deploy in the same session is still caught.
 */
const RELOAD_COOLDOWN_MS = 60000;

@Injectable({
  providedIn: 'root',
})
export class AppReloadService implements OnDestroy {
  private promptOpen = false;
  private subscription = new Subscription();

  constructor(
    private router: Router,
    private toastService: ToastService,
    private zone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.stopWatching();
  }

  /**
   * Starts watching for failures caused by bundles that no longer exist on the host.
   *
   * @remarks
   * Web only. Native builds load their bundles off the local filesystem, so they cannot go
   * stale and a reload prompt would make no sense there.
   *
   * Covers the three ways a failed bundle fetch reaches the app:
   * 1. Router `NavigationError` — every page is a lazy route, so this is the common case.
   * 2. `unhandledrejection` — Ionic loads its own component chunks with native `import()`,
   *    and those failures never reach Angular.
   * 3. `ErrorHandler` — see `StaleBundleErrorHandler`, which delegates here for anything
   *    else, such as the lazy `heic-to` import in `image-conversion.helper.ts`.
   */
  watchForStaleBundle(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
        .subscribe((event) => this.handleError(event.error))
    );

    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  stopWatching(): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();

    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  /**
   * Offers the user a reload when an error turns out to be a stale bundle.
   *
   * @param error - The error to inspect. Ignored unless it looks like a failed bundle fetch.
   *
   * @remarks
   * The user is prompted rather than reloaded automatically. A failed navigation leaves them
   * on the page they started from, which may hold unsaved form input, so the reload has to be
   * their decision. That also means a reload loop would need repeated deliberate clicks —
   * {@link RELOAD_COOLDOWN_MS} is there to stop the prompt nagging, not to prevent a loop.
   */
  async handleError(error: unknown): Promise<void> {
    if (Capacitor.isNativePlatform() || this.promptOpen || !isChunkLoadError(error)) {
      return;
    }

    if (this.hasRecentlyReloaded()) {
      logger.log(error, 'AppReloadService.handleError -> stale bundle, still in cooldown');

      return;
    }

    logger.log(error, 'AppReloadService.handleError -> stale bundle, prompting for reload');

    this.promptOpen = true;

    try {
      const result = await this.toastService.showToast({
        header: 'Update Available',
        message: 'A newer version of Lyfe Plum was released. Reload to continue.',
        // Persist until the user chooses; there is no useful default action.
        duration: 0,
        position: 'bottom',
        color: 'warning',
        buttons: [{ text: 'Reload', role: 'reload' }],
      });

      if (result.role === 'reload') {
        this.reload();
      }
    } finally {
      this.promptOpen = false;
    }
  }

  private onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }

    // Fired outside Angular, so the toast would not trigger change detection on its own.
    this.zone.run(() => this.handleError(event.reason));
  };

  private reload(): void {
    try {
      sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    } catch (error: unknown) {
      // Storage can be unavailable in private windows. The prompt already required a
      // deliberate click, so losing the cooldown is acceptable.
      logger.log(error, 'AppReloadService.reload -> could not record the reload');
    }

    window.location.reload();
  }

  private hasRecentlyReloaded(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);

      return Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS;
    } catch (error: unknown) {
      logger.log(error, 'AppReloadService.hasRecentlyReloaded -> storage unavailable');

      return false;
    }
  }
}
